<template>
  <div
    ref="panel"
    class="resizable-panel"
    :class="{ 'is-resizing': isResizing, 'dark-theme': isDark }"
    :style="panelStyle"
  >
    <div
      class="resize-handle"
      :class="position"
      @mousedown="startResize"
    >
      <div class="resize-indicator" />
    </div>
    <div class="panel-content">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResizablePanel',
  props: {
    initialWidth: {
      type: Number,
      default: 360,
    },
    minWidth: {
      type: Number,
      default: 200,
    },
    maxWidthPercent: {
      type: Number,
      default: 80, // 80% of container
    },
    position: {
      type: String,
      default: 'left',
      validator: val => ['left', 'right'].includes(val),
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    storageKey: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      width: this.initialWidth,
      isResizing: false,
      startX: 0,
      startWidth: 0,
      containerWidth: 0,
    };
  },
  computed: {
    panelStyle() {
      return {
        width: `${this.width}px`,
        flexBasis: `${this.width}px`,
      };
    },
    maxWidth() {
      return this.containerWidth > 0
        ? Math.floor(this.containerWidth * (this.maxWidthPercent / 100))
        : 800;
    },
  },
  mounted() {
    this.updateContainerWidth();
    this.loadSavedWidth();
    window.addEventListener('resize', this.updateContainerWidth);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.stopResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerWidth);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.stopResize);
  },
  methods: {
    updateContainerWidth() {
      const parent = this.$refs.panel?.parentElement;
      if (parent) {
        this.containerWidth = parent.offsetWidth;
      }
    },
    loadSavedWidth() {
      if (this.storageKey) {
        const saved = localStorage.getItem(this.storageKey);
        if (saved) {
          const parsed = parseInt(saved, 10);
          if (!Number.isNaN(parsed) && parsed >= this.minWidth) {
            this.width = Math.min(parsed, this.maxWidth);
          }
        }
      }
    },
    saveWidth() {
      if (this.storageKey) {
        localStorage.setItem(this.storageKey, this.width.toString());
      }
    },
    startResize(event) {
      event.preventDefault();
      this.updateContainerWidth();
      this.isResizing = true;
      this.startX = event.clientX;
      this.startWidth = this.width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      this.$emit('resize-start');
    },
    onMouseMove(event) {
      if (!this.isResizing) return;

      const deltaX = event.clientX - this.startX;
      let newWidth;

      if (this.position === 'left') {
        newWidth = this.startWidth - deltaX;
      } else {
        newWidth = this.startWidth + deltaX;
      }

      newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, newWidth));
      this.width = newWidth;
      this.$emit('resize', this.width);
    },
    stopResize() {
      if (this.isResizing) {
        this.isResizing = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        this.saveWidth();
        this.$emit('resize-end', this.width);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resizable-panel {
  position: relative;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  overflow: hidden;

  &.is-resizing {
    user-select: none;
  }
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;

  &.left {
    left: 0;
  }

  &.right {
    right: 0;
  }

  &:hover,
  .is-resizing & {
    background-color: rgba(var(--brand-primary-rgb, 59, 130, 246), 0.15);

    .resize-indicator {
      opacity: 1;
      background-color: var(--brand-primary, #3b82f6);
    }
  }
}

.resize-indicator {
  width: 3px;
  height: 40px;
  border-radius: 2px;
  background-color: var(--border-secondary, #d1d5db);
  opacity: 0.5;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.panel-content {
  flex: 1;
  height: 100%;
  overflow: hidden;
}

// Dark theme adjustments
.dark-theme {
  .resize-handle {
    &:hover,
    .is-resizing & {
      background-color: rgba(0, 245, 255, 0.1);

      .resize-indicator {
        background-color: #00f5ff;
      }
    }
  }

  .resize-indicator {
    background-color: rgba(255, 255, 255, 0.2);
  }
}
</style>
